/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/ui/OfflineBanner.tsx
 *
 * Rôle :
 *   Bannière discrète affichée en haut/bas de page selon l'état du réseau :
 *     - Hors ligne                   → fond ambré, message "Hors ligne".
 *     - Synchronisation en cours     → message "Synchronisation…".
 *     - Items en attente (de retour) → "X actions à synchroniser…".
 *     - Tout va bien                 → composant null (pas affiché).
 *
 *   Accessibilité : `role="status"` + `aria-live="polite"` (état réseau =
 *   info non urgente). Pas de focus volé, pas de modal.
 *
 * Réutilisable :
 *   100 %. Aucun couplage TNE.
 *
 * @author Mika
 * @since 2026-05-04
 */

import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import styles from './OfflineBanner.module.css';

export function OfflineBanner() {
  const { online, draining, pendingCount } = useOnlineStatus();

  // Cas 1 : tout va bien — rien à afficher.
  if (online && !draining && pendingCount === 0) {
    return null;
  }

  // Cas 2 : synchronisation en cours.
  if (draining) {
    return (
      <div className={`${styles.banner} ${styles.syncing}`} role="status" aria-live="polite">
        <span className={styles.dot} aria-hidden="true" />
        Synchronisation en cours…
      </div>
    );
  }

  // Cas 3 : hors ligne.
  if (!online) {
    return (
      <div className={`${styles.banner} ${styles.offline}`} role="status" aria-live="polite">
        <span className={styles.dot} aria-hidden="true" />
        Mode hors ligne — vos actions seront envoyées au retour de connexion
        {pendingCount > 0 && ` (${pendingCount} en attente)`}.
      </div>
    );
  }

  // Cas 4 : en ligne mais des items résiduels.
  return (
    <div className={`${styles.banner} ${styles.queued}`} role="status" aria-live="polite">
      <span className={styles.dot} aria-hidden="true" />
      {pendingCount} action{pendingCount > 1 ? 's' : ''} à synchroniser…
    </div>
  );
}
